import React, { useState, useEffect } from "react";
import { observer } from "mobx-react";
import { useHistory } from "react-router-dom";
import { agentsData } from "Stores/agentsData";
import { userDetail } from "Stores/userDetail";
import { fetchLinkedInAccounts } from "Utils/commonAPI";
import AgentSelect from "./AgentSelect";
import { DEFAULT_DAYS, defaultTimezone } from "./DayOfWeekField";
import { SetupPage, When, Step, Field, Row, Schedule, SequenceField, NotifyStep, openInBuilder, clampInt, Need } from "./SetupFlow";

// The three LinkedIn import sources, with the copy that changes per source.
const SOURCES = {
  search: {
    label: "Sales Navigator search",
    urlLabel: "Sales Navigator search URL",
    placeholder: "https://www.linkedin.com/sales/search/people?...",
    hint: "Paste the URL of a Sales Navigator people search.",
  },
  post: {
    label: "Post engagers",
    urlLabel: "LinkedIn post URL",
    placeholder: "https://www.linkedin.com/posts/...",
    hint: "Imports the people who reacted to or commented on the post.",
  },
  event: {
    label: "Event attendees",
    urlLabel: "LinkedIn event URL",
    placeholder: "https://www.linkedin.com/events/...",
    hint: "Imports the people attending the event.",
  }
};

const CreateLinkedInImport = observer(() => {
  const history = useHistory();
  const [name, setName] = useState("");
  const [sourceType, setSourceType] = useState("search"); // search | post | event
  const [url, setUrl] = useState("");
  const [askEachRun, setAskEachRun] = useState(true); // default on — import a different post/event each run
  const [engagementType, setEngagementType] = useState("all");
  const [listId, setListId] = useState("");
  const [sequenceId, setSequenceId] = useState("");
  const [sequenceName, setSequenceName] = useState("");
  const [runWhen, setRunWhen] = useState("MANUAL"); // MANUAL | DAILY | WEEKLY
  const [time, setTime] = useState("08:00");
  const [days, setDays] = useState(DEFAULT_DAYS);
  const [dailyCap, setDailyCap] = useState(25);
  const [notifyMode, setNotifyMode] = useState("ON_FAILURE");
  const [saving, setSaving] = useState(false);
  const timezone = defaultTimezone();

  useEffect(() => {
    fetchLinkedInAccounts(); // powers the connect-account warning below
    agentsData.fetchContactLists();
  }, []);

  const src = SOURCES[sourceType];
  const manual = runWhen === "MANUAL";
  const effAsk = manual && askEachRun; // "ask each run" is manual-only
  const urlReady = effAsk || !!url.trim();

  // LinkedIn connection state → a gentle pre-warning (create is still allowed; it's a draft).
  const liAccounts = userDetail.activeLinkedInAccounts || [];
  const hasLinkedIn = liAccounts.length > 0;
  const hasSalesNav = liAccounts.some(a => a && a.linkedinAccountType
    && String(a.linkedinAccountType).toLowerCase() !== "classic");
  const accountWarn = sourceType === "search"
    ? (hasSalesNav ? null : "This source needs a connected Sales Navigator account.")
    : (hasLinkedIn ? null : "This source needs a connected LinkedIn account.");

  const hasDestination = !!listId || !!sequenceId; // imported contacts must land somewhere findable
  const canSubmit = urlReady && hasDestination;

  const buildPayload = () => ({
    name: name.trim() || ("Import from LinkedIn · " + src.label),
    sourceType,
    askEachRun: effAsk,
    url: effAsk ? undefined : url.trim(),
    engagementType: sourceType === "post" ? engagementType : undefined,
    listId: listId || undefined,
    sequenceId: sequenceId || undefined,
    dailyCap: clampInt(dailyCap, 1, null, 25),
    notifyMode,
    timezone,
    ...(manual ? {} : { frequency: runWhen, time, ...(runWhen === "WEEKLY" ? { days } : {}) })
  });

  const submit = async () => {
    if (!canSubmit) return;
    setSaving(true);
    const agent = await agentsData.createLinkedinImport(buildPayload());
    setSaving(false);
    if (agent && agent.id) history.push(`/agents/${agent.id}`);
  };

  const customize = () => openInBuilder(history, "linkedin-import", buildPayload(), setSaving);
  const pickRun = v => { setRunWhen(v); if (v !== "MANUAL") setAskEachRun(false); };

  return (
    <SetupPage
      title="Hire · Import from LinkedIn"
      name={{ value: name, onChange: setName, placeholder: "Import from LinkedIn · " + src.label }}
      bar={{
        canSubmit, saving, onCreate: submit, onCustomize: customize, onCancel: () => history.push("/agents"),
        reason: !urlReady ? "Paste a LinkedIn URL to create it." : "Pick a list or a sequence for the contacts."
      }}>
      <When icon="clock">
        <Schedule runWhen={runWhen} setRunWhen={pickRun} days={days} setDays={setDays} time={time} setTime={setTime} timezone={timezone}
          options={[
            { value: "MANUAL", label: "Only when I run it" },
            { value: "DAILY", label: "Every day" },
            { value: "WEEKDAYS", label: "Weekdays (Mon–Fri)" },
            { value: "WEEKLY", label: "Specific days" }
          ]} />
      </When>
      <Step icon="linkedin" tone="violet" title="Import people from LinkedIn">
        <Row>
          <Field>
            <AgentSelect value={sourceType} onChange={setSourceType} options={[
              { value: "search", label: "A Sales Navigator search" },
              { value: "post", label: "People who engaged with a post" },
              { value: "event", label: "People attending an event" }
            ]} />
          </Field>
          {sourceType === "post" && (
            <Field>
              <AgentSelect value={engagementType} onChange={setEngagementType} options={[
                { value: "all", label: "Reactions and comments" },
                { value: "reactions", label: "Only reactions" },
                { value: "comments", label: "Only comments" }
              ]} />
            </Field>
          )}
        </Row>
        {accountWarn && (
          <span className="ag-sf__hint is-warn">{accountWarn}{" "}
            <a className="ag-link" href="/settings/account/linkedin" onClick={e => { e.preventDefault(); history.push("/settings/account/linkedin"); }}>Connect it</a>
          </span>
        )}
        {effAsk
          ? <span className="ag-sf__hint">You will paste the {src.urlLabel} each time you run it.</span>
          : (
            <Field>
              <input className={`ag-input${url.trim() ? "" : " ag-sf__need"}`} value={url} placeholder={src.placeholder} onChange={e => setUrl(e.target.value)} aria-label={src.urlLabel} />
            </Field>
          )}
        {manual && (
          <label className="ag-sf__check">
            <input type="checkbox" checked={askEachRun} onChange={e => setAskEachRun(e.target.checked)} />
            <span>Ask me for the URL each time I run it</span>
          </label>
        )}
        <Field label="Contacts per run">
          <input className="ag-input ag-sf__num" type="number" min="1" value={dailyCap} onChange={e => setDailyCap(e.target.value)} />
        </Field>
      </Step>
      <Step icon="list" tone="green" title="Add them to a list" tags={[sequenceId && { label: "Optional" }]}>
        <Field>
          <Need ok={!!listId || !!sequenceId}><AgentSelect searchable placeholder="Pick a contact list" value={listId}
            onChange={setListId}
            options={(agentsData.contactLists || []).map(l => ({ value: l.id, label: l.count ? `${l.name} · ${l.count}` : l.name }))} /></Need>
        </Field>
        {listId && <button type="button" className="ag-link ag-sf__clear" onClick={() => setListId("")}>Clear list</button>}
      </Step>
      <Step icon="send" tone="amber" title="Add to a sequence" tags={[{ label: "Optional" }]}>
        <SequenceField id={sequenceId} name={sequenceName} placeholder="No sequence"
          onPick={s => { setSequenceId(s.id); setSequenceName(s.name); }}
          onClear={() => { setSequenceId(""); setSequenceName(""); }} clearLabel="Clear sequence" />
      </Step>
      <NotifyStep value={notifyMode} onChange={setNotifyMode} />
    </SetupPage>
  );
});

export default CreateLinkedInImport;
